import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, useLocation } from 'react-router-dom'
import type { Role } from '@/auth/roles'
import { useAuth } from '@/auth/useAuth'
import { Spinner } from '@/components/Spinner'

/**
 * A convenience, not a security control: it decides what to render, never what is allowed. The
 * backend re-checks token and roles on every request.
 *
 * `role` is matched against the effective roles, so an ADMIN passes a `role="SALES"` gate.
 */
export function RequireAuth({ children, role }: { children: ReactNode; role?: Role }) {
  const { t } = useTranslation()
  const { status, hasRole } = useAuth()
  const location = useLocation()

  if (status === 'loading') {
    return <Spinner label={t('auth.checking')} />
  }
  // Not a redirect: the logout redirect is already on its way, and sending the user to /login
  // here would start a competing login.
  if (status === 'signingOut') {
    return <Spinner label={t('auth.signingOut')} />
  }
  if (status === 'anonymous') {
    return <Navigate to="/login" replace state={{ returnTo: location.pathname + location.search }} />
  }
  if (role && !hasRole(role)) {
    return <Navigate to="/forbidden" replace />
  }
  return <>{children}</>
}
